import { Date, InlineUser } from "@/components";
import {
  HistoryLogActionLabel as ActionLabel,
  FormType,
  HistoryEntity,
  HistoryLogAction,
  PROJECT_STATUS_DICT,
} from "@/enums";
import { IHistoryLog, IHistoryLogTableModel } from "@/interfaces";
import {
  useGetAdminFlatFormsQuery,
  useGetFormLookupsQuery,
  useGetHistoryLogsByIdQuery,
} from "@/services";
import { HistoryLogPropertyDiff } from "@/types";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Spin, Table, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { diff } from "deep-diff";
import _ from "lodash";
import { ReactNode, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Helpers from "./Helpers";
import { HistoryLogDiffTable } from "./HistoryLogDiffTable";

interface IProps {
  parentEntityId: string;
  additionalLabels?: { [key: string]: string };
  pageSize?: number;
}

interface IValueObject {
  [key: string]: string;
}

const rowExpandableDisabled: { [key: string]: boolean } = {
  [HistoryEntity.FormTranslation]: true,
  [HistoryEntity.UserAssignment]: true,
  [HistoryEntity.ApprovalWorkFlowApprover]: true,
  [HistoryEntity.DeleteWorkFlowApprover]: true,
};

function fallbackDisplay(value?: string | null, key?: string) {
  if (!value) {
    return (
      <em key={key}>
        <FormattedMessage id="general.nothing" defaultMessage={"Nothing"} />
      </em>
    );
  }
  return (
    <em key={key}>
      <b>{value}</b>
    </em>
  );
}

const HistoryLogTable = ({
  parentEntityId,
  additionalLabels,
  pageSize,
}: IProps) => {
  const intl = useIntl();
  const { data: lookups, isFetching: isLoadingForm } = useGetFormLookupsQuery();

  const {
    data: changeLogs,
    isFetching,
    refetch,
  } = useGetHistoryLogsByIdQuery({
    parentEntityId: parentEntityId,
  });

  const {
    data: { flatForms, categories } = {},
    isFetching: loadingCategories,
  } = useGetAdminFlatFormsQuery({});

  const formValuesDict: { [id: string]: string } = useMemo(() => {
    if (
      _.isNil(lookups) ||
      _.isNil(lookups[FormType.Intake]) ||
      isLoadingForm ||
      loadingCategories
    )
      return {};

    const categoryLabels =
      _.mapValues(_.keyBy(categories, "id"), (x) => x.label?.fallback) || {};
    const propertyLabels =
      _.mapValues(
        _.keyBy(flatForms, (x) => x.property.id),
        (x) => x.property.label?.fallback
      ) || {};
    let allValues = { ...categoryLabels, ...propertyLabels };

    Object.keys(lookups[FormType.Intake]).forEach((key) => {
      const formPropertyValues = lookups[FormType.Intake][key];
      Object.keys(formPropertyValues).forEach((valueKey) => {
        allValues = {
          ...allValues,
          [valueKey]: formPropertyValues[valueKey].fallback,
        };
      });
    });

    return allValues;
  }, [categories, flatForms, isLoadingForm, loadingCategories, lookups]);

  const allLookupValues = useMemo(() => {
    const labelArray = Object.keys(PROJECT_STATUS_DICT).map((key) => ({
      key: key,
      label: intl.formatMessage({
        id: PROJECT_STATUS_DICT[key],
      }),
    }));

    const projectLabels = _.mapValues(_.keyBy(labelArray, "key"), "label");

    return { ...formValuesDict, ...additionalLabels, ...projectLabels };
  }, [additionalLabels, formValuesDict, intl]);

  const getValue = useCallback(
    (value: string) => {
      if (_.isNil(value)) return value;

      if (Array.isArray(value)) {
        const displayValues = value.map((key) => allLookupValues?.[key] || key);
        return displayValues.join(", ");
      }

      return allLookupValues?.[value] || value;
    },
    [allLookupValues]
  );

  const calculateDifferences = useCallback(
    (
      formChange: IHistoryLog,
      parsedOldValue: IValueObject,
      parsedNewValue: IValueObject
    ): HistoryLogPropertyDiff[] => {
      const keys = new Set(
        [
          (parsedOldValue && Object.keys(parsedOldValue)) || [],
          (parsedNewValue && Object.keys(parsedNewValue)) || [],
        ].flat()
      );

      const arrayProperties: { [key: string]: HistoryLogPropertyDiff } = {};
      keys.forEach((key: string) => {
        if (
          Array.isArray(parsedOldValue[key]) ||
          Array.isArray(parsedNewValue[key])
        ) {
          arrayProperties[key] = {
            id: `${formChange.id}-${key}`,
            path: [key],
            property: key,
            isAdGroup: key.includes("AdGroupAssignments"),
            isUserId: false,
            oldValue: getValue(parsedOldValue[key]),
            newValue: getValue(parsedNewValue[key]),
            kind: "E",
            lhs: parsedOldValue[key],
            rhs: parsedNewValue[key],
          };
        }
      });

      const differences = (diff(parsedOldValue, parsedNewValue) || [])
        .filter((difference) => !arrayProperties[difference.path?.[0]])
        .map((difference, index) => {
          const path = difference.path || [];
          return {
            id: `${formChange.id}-${index}`,
            ...difference,
            path: path,
            property: path.join("_"),
            isAdGroup: path.includes("AdGroupAssignments"),
            isUserId:
              path.includes("ReplaceBy") 
                || path.includes("UserToBeReplaced")
              || path.includes("OwnerId"),
            oldValue: getValue(
              // @ts-ignore: Unreachable code error
              _.isNil(difference.lhs) ? difference.item?.lhs : difference.lhs
            ),
            newValue: getValue(
              // @ts-ignore: Unreachable code error
              _.isNil(difference.rhs) ? difference.item?.rhs : difference.rhs
            ),
          };
        });

      return [...differences, ...Object.values(arrayProperties)];
    },
    [getValue]
  );

  const changeLogValues = useMemo(() => {
    const changeLogsDisplay: IHistoryLogTableModel[] = changeLogs?.map(
      (formChange: IHistoryLog): IHistoryLogTableModel => {
        const parsedOldValue = formChange.oldValue
          ? { ...JSON.parse(formChange.oldValue) }
          : undefined;
        const parsedNewValue = formChange.newValue
          ? { ...JSON.parse(formChange.newValue) }
          : undefined;

        const actionLabel = intl.formatMessage({
          id: `historyLog.${ActionLabel[formChange.action]}PastTense`,
        });

        const userAssignmentActionLabel = intl.formatMessage({
          id: `historyLog.${
            formChange.action === HistoryLogAction.Create ? "added" : "removed"
          }`,
        });

        function getChanged(): ReactNode {
          switch (formChange.entityType) {
            case HistoryEntity.FormTranslation: {
              const arrValues = _.split(formChange.entityId, ".");
              const value = arrValues[arrValues.length - 2];
              const translationLabel = Helpers.FormTranslationLabel[value];
              return (
                <>
                  <FormattedMessage id={`newFormField.${translationLabel}`} />
                  <FormattedMessage
                    id={`historyLog.changeSummary`}
                    values={{
                      oldValue: fallbackDisplay(
                        parsedOldValue.Fallback,
                        "oldValue"
                      ),
                      newValue: fallbackDisplay(
                        parsedNewValue.Fallback,
                        "newValue"
                      ),
                    }}
                  />
                </>
              );
            }
            case HistoryEntity.EditableByStatus: {
              return (
                <FormattedMessage
                  id={`historyLog.permissionStatusChange`}
                  values={{
                    action: actionLabel,
                    permission: formValuesDict[formChange.entityId],
                  }}
                />
              );
            }
            case HistoryEntity.FormPropertyValue: {
              return (
                <FormattedMessage
                  id={`historyLog.optionsChange`}
                  values={{
                    action: actionLabel,
                    option: formValuesDict[formChange.entityId],
                  }}
                />
              );
            }
            case HistoryEntity.Permission: {
              return (
                <FormattedMessage
                  id={`historyLog.permissionChange`}
                  values={{
                    action: actionLabel,
                  }}
                />
              );
            }
            case HistoryEntity.UserAssignment: {
              const translationId =
                parsedNewValue.Exclude || parsedOldValue.Exclude
                  ? `historyLog.excludeUsersChange`
                  : `historyLog.addUsersChange`;
              return (
                <FormattedMessage
                  id={translationId}
                  values={{
                    user: (
                      <b key="user">
                        <InlineUser
                          userId={
                            parsedNewValue.UserId || parsedOldValue.UserId
                          }
                        />
                      </b>
                    ),
                    action: userAssignmentActionLabel,
                  }}
                />
              );
            }
            case HistoryEntity.DeleteWorkFlowApprover:
            case HistoryEntity.ApprovalWorkFlowApprover: {
              return (
                <FormattedMessage
                  id={"historyLog.approverChange"}
                  values={{
                    role: (
                      <b key="role">
                        {getValue(
                          parsedNewValue.SecondaryId ||
                            parsedOldValue.SecondaryId
                        )}
                      </b>
                    ),
                    action: userAssignmentActionLabel,
                  }}
                />
              );
            }
            case HistoryEntity.UserReplacementHistory: {
              return (
                <FormattedMessage
                  id={`historyLog.userReplacementChange`}
                  values={{
                    user: (
                      <b key="user">
                        <InlineUser userId={parsedNewValue.UserToBeReplaced} />
                      </b>
                    ),
                    replacement: parsedNewValue.ReplaceBy ? (
                      <b key="replacement">
                        <InlineUser userId={parsedNewValue.ReplaceBy} />
                      </b>
                    ) : (
                      "Nothing"
                    ),
                  }}
                />
              );
            }
            case HistoryEntity.FormProperty:
            default:
              return (
                <FormattedMessage
                  id={`historyLog.fieldChange`}
                  values={{ action: actionLabel }}
                />
              );
          }
        }

        return {
          ...formChange,
          differences: calculateDifferences(
            formChange,
            parsedOldValue,
            parsedNewValue
          ),
          changed: getChanged(),
        };
      }
    );

    return _.orderBy(changeLogsDisplay, (t) => t.timestamp, "desc");
  }, [calculateDifferences, changeLogs, formValuesDict, getValue, intl]);

  const columns: ColumnsType<IHistoryLogTableModel> = [
    {
      title: <FormattedMessage id="historyLog.changedByCol" />,
      render: (_, record) => <InlineUser userId={record.userId} />,
      key: "UserId",
    },
    {
      title: <FormattedMessage id="historyLog.dateCol" />,
      render: (_, record) => (
        <Date value={record.timestamp} showSecond={true} />
      ),
      key: "Timestamp",
      width: 190,
    },
    {
      title: <FormattedMessage id="historyLog.ActionCol" />,
      render: (_, record) => (
        <Typography.Text>
          <FormattedMessage id={`historyLog.${ActionLabel[record.action]}`} />
        </Typography.Text>
      ),
      key: "Action",
      width: 85,
    },
    {
      title: <FormattedMessage id="historyLog.summaryCol" />,
      dataIndex: "changed",
      key: "Changed",
    },
  ];

  if (isFetching) <Spin size="large" />;

  return (
    <>
      <Typography.Title level={4}>
        <FormattedMessage id="general.history" />
        <Button
          data-test-id={`refresh-history-button`}
          type="link"
          onClick={() => refetch()}
          icon={<FontAwesomeIcon icon={faRefresh} />}
        />
      </Typography.Title>
      <Table
        dataSource={changeLogValues}
        columns={columns}
        rowKey={(changelog: IHistoryLogTableModel) => changelog.id}
        pagination={{ pageSize: pageSize || 5, showSizeChanger: false }}
        loading={isFetching}
        expandable={{
          expandedRowRender: (record: IHistoryLogTableModel) => (
            <HistoryLogDiffTable
              data-test-id={`history-changelog-diff-table-${record.id}`}
              historyLog={record}
              pagination={false}
            />
          ),
          rowExpandable: (record: IHistoryLogTableModel) =>
            !rowExpandableDisabled[record.entityType],
        }}
      />
    </>
  );
};

export default HistoryLogTable;
